/**
 * Progress bar for event form completion.
 * Shows filled vs total required fields.
 */

export interface EventEditorProgressProps {
  filledCount: number;
  totalCount: number;
  variant?: "linear" | "circular";
  className?: string;
}

export function EventEditorProgress({
  filledCount,
  totalCount,
  variant = "linear",
  className = "",
}: EventEditorProgressProps) {
  const percent = totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0;

  if (variant === "circular") {
    const size = 36;
    const stroke = 3;
    const r = (size - stroke) / 2;
    const circumference = 2 * Math.PI * r;
    const offset = circumference - (percent / 100) * circumference;

    return (
      <div className={`relative inline-flex items-center justify-center ${className}`}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            className="text-slate-200"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="text-[var(--color-accent)] transition-[stroke-dashoffset] duration-300"
          />
        </svg>
        <span className="absolute text-[10px] font-semibold text-slate-600 tabular-nums">
          {percent}%
        </span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 min-w-0 ${className}`}>
      <div className="flex-1 min-w-0 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-[var(--color-accent)] rounded-full transition-[width] duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-[11px] font-medium text-slate-500 tabular-nums flex-shrink-0">
        {filledCount}/{totalCount}
      </span>
    </div>
  );
}
